import { useCallback, useEffect, useState } from 'react';
import { TSortValue } from '~/commonComponents';
import {
  CandidateSortableField,
  CandidateStatus,
  useGetCandidateList,
} from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { useEnablePermanentJobMMFeature } from '~/hooks';

const DEFAULT_SORTING: TSortValue = {
  direction: 'DESC',
  field: CandidateSortableField.Shortlist,
};

export const useCandidateTabView = (jobPostingId: string) => {
  const [formStatus, setFormStatus] = useState<CandidateStatus | 'all'>('all');
  const [formSorting, setFormSorting] = useState<TSortValue>(DEFAULT_SORTING);
  const [currentPage, setCurrentPage] = useState(1);

  const { isEnablePermanentJobMM } = useEnablePermanentJobMMFeature();

  const allStatuses = isEnablePermanentJobMM
    ? [
        CandidateStatus.AppExpired,
        CandidateStatus.Applied,
        CandidateStatus.Interviewed,
        CandidateStatus.Scheduled,
      ]
    : undefined;

  const { data, error, isError, isInitialLoading, isLoading, pagination, statistics } =
    useGetCandidateList({
      jobPostingId,
      orderBy: formSorting.field as CandidateSortableField,
      orderDirection: formSorting.direction,
      page: currentPage,
      status: formStatus !== 'all' ? [formStatus] : allStatuses,
    });

  const handleStatusFilterChange = useCallback((status: CandidateStatus | 'all') => {
    setFormStatus(status);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSetFormSorting = (value: TSortValue | undefined) => {
    setFormSorting(value === undefined ? DEFAULT_SORTING : value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (isError) {
      showErrorMessage({ error });
    }
  }, [isError, error]);

  return {
    data,
    filters: {
      sorting: formSorting,
      status: formStatus,
    },
    isInitialLoading,
    isLoading,
    methods: {
      setCurrentPage: handlePageChange,
      setStatusFilter: handleStatusFilterChange,
      setSorting: handleSetFormSorting,
    },
    pagination: {
      currentPage,
      totalPages: pagination.totalPages,
    },
    statistics,
  };
};
